import { addDays, parse, startOfWeek } from 'date-fns';
import {
  createContext,
  useContext,
  useState,
  FC,
  ReactNode,
  useMemo,
  useEffect,
  useRef
} from 'react';
import { useRouter } from 'next/router';
import { useDisclosure } from '@gettactic/helpers/src';
import { UseDisclosureReturn } from '@gettactic/helpers/src';
import { useAllUserSettings, useSaveUserSettings } from '@/hooks/useUserSettings';
import { useQueryClient } from '@tanstack/react-query';
import { PossibleDateSort, UserPropertyType } from '@gettactic/api';
import { useAuthenticated } from '@gettactic/hooks';

export interface DashboardFilters {
  weekDate: string;
  week: Date[];
  teams: string[];
  office: string | null;
  users: string[];
  currentPage?: string;
  selectedPageId?: string;
  selectedSectionId?: string | null;
  dateToSortBy?: string;
  dateSortOrder?: PossibleDateSort;
}

interface DashboardFiltersContextProps {
  filters: DashboardFilters;
  // TBD: fix any type
  selectedEvent: any;
  setSelectedEvent: (event: any) => void;
  setWeekDate: (date: string) => void;
  setTeams: (teams: string[]) => void;
  setOffice: (office: string | null) => void;
  setUsers: (users: string[]) => void;
  setCurrentPage: (page: DashboardFilters['currentPage']) => void;
  setSelectedPageId: (pageId: DashboardFilters['selectedPageId']) => void;
  setSelectedSectionId: (
    sectionId: DashboardFilters['selectedSectionId']
  ) => void;
  page: PageOptions | null;
  setPage: (page: PageOptions | null) => void;
  agendaDisclosure?: UseDisclosureReturn;
  sortByDate: (dateSort: string | undefined, sortOrder?: PossibleDateSort) => void;
  toggleSortOrder: () => void;
}

const DashboardFiltersContext = createContext<
  DashboardFiltersContextProps | undefined
>(undefined);

interface DashboardStateProviderProps {
  children: ReactNode;
  defaultFilters?: DashboardFilters;
  hideWeekends?: boolean;
  selectedEvent: any;
  setSelectedEvent: (event: any) => void;
}

export const dashboardStateFallbackValues = {
  weekDate: new Date().toISOString().split('T')[0],
  teams: [],
  office: null,
  users: [],
  week: [],

  dateToSortBy: undefined,
  dateSortOrder: 'asc' as PossibleDateSort,

  // @todo: remove this to currentTab, because page in this context
  // is references to site info pages
  currentPage: 'Attendance',

  // site info page attributes
  selectedPageId: undefined,
  selectedSectionId: undefined,

};

export type PageOptions = 'Attendance' | 'Visitors' | 'News' | 'SiteInfo';

export type DashQueryParams = {
  dashboard_tab: PageOptions;
  office: string | null;

  selected_section?: string | null;
  selected_page?: string | null;
}

export const DashboardStateProvider: FC<DashboardStateProviderProps> = ({
  children,
  hideWeekends = false,
  defaultFilters = dashboardStateFallbackValues,
  selectedEvent = null,
  setSelectedEvent = () => {}
}) => {
  const router = useRouter();

  const { setUserContext } = useAuthenticated();

  const queryClient = useQueryClient();
  const { userSettingsByType, userSettings } = useAllUserSettings();
  const saveUserSettings = useSaveUserSettings(queryClient);

  const agendaDisclosure = useDisclosure();
  const [weekDate, setWeekDate] = useState<string>(defaultFilters.weekDate);

  const [dateToSortBy, setDateToSortBy] = useState<string | undefined>(defaultFilters.weekDate);
  const [dateSortOrder, setDateSortOrder] = useState<PossibleDateSort>(PossibleDateSort.ASC);
  
  const [teams, setTeams] = useState<string[]>(defaultFilters.teams);
  const [users, setUsers] = useState<string[]>(defaultFilters.users);

  const teamsSetFromSettings = useRef(false);
  const usersSetFromSettings = useRef(false);

  const [office, setOffice] = useState<string | null>(
    defaultFilters.office
  );

  const sortByDate = (
    dateSort: string | undefined,
    sortOrder: PossibleDateSort = PossibleDateSort.ASC
  ) => {
    if(dateSort === dateToSortBy) {
      toggleSortOrder();
    } else {
      setDateToSortBy(dateSort);
      setDateSortOrder(sortOrder);
    }
  }

  const toggleSortOrder = () => {
    if(dateSortOrder === PossibleDateSort.ASC) {
      setDateSortOrder(PossibleDateSort.DESC);
    } else if(dateSortOrder === PossibleDateSort.DESC) {
      setDateSortOrder(PossibleDateSort.ASC);
      setDateToSortBy(undefined);
    } else {
      setDateSortOrder(PossibleDateSort.ASC);
    }
  }

  useEffect(() => {
    if(userSettings && userSettings.length > 0) {
      setTeams(userSettingsByType?.[UserPropertyType.LastTeamsFilter] || defaultFilters.teams);
      teamsSetFromSettings.current = true;
    }

    if(userSettingsByType?.[UserPropertyType.LastUsersFilter] && !usersSetFromSettings.current) {
      setUsers(userSettingsByType?.[UserPropertyType.LastUsersFilter] || defaultFilters.users);
      usersSetFromSettings.current = true;
    }
  }, [userSettings]);
  
  
  const [page, setPage] = useState<PageOptions>('Attendance');
  const [currentPage, setCurrentPage] = useState<string>(
    defaultFilters.currentPage || 'Attendance'
  );
  const [selectedPageId, setSelectedPageId] = useState<string | undefined>(
    defaultFilters.selectedPageId
  );
  const [selectedSectionId, setSelectedSectionId] = useState<
    string | null | undefined
  >(defaultFilters?.selectedSectionId ?? null);

  // Refs to prevent update loops
  const isUpdatingFromUrl = useRef(false);
  const lastQueryString = useRef('');

  const parsedDate = parse(weekDate, 'yyyy-MM-dd', new Date());
  const firstMondayBefore = startOfWeek(parsedDate, { weekStartsOn: 1 });

  // @todo: This is a hack because the API is not returning
  // the complete week
  // this comes from long time ago
  // @todo: remove this once the API is fixed
  const week = useMemo(() => {
    const week = [];
    const days = hideWeekends ? 5 : 7;
    for (let i = 0; i < days; i++) {
      week.push(addDays(firstMondayBefore, i));
    }
    return week;
  }, [weekDate, firstMondayBefore, hideWeekends]);

  // When on Site Info tab, remember which office was selected
  // and remember the selected section and page
  useEffect(() => {
    if (!router.isReady) return;

    // Skip if we're updating from URL to prevent loops
    if (isUpdatingFromUrl.current) return;

    // on any other tab, just save the current tab
    if(page !== 'SiteInfo') {
      window.history.replaceState(null, '', `?dashboard_tab=${page}`);
      return;
    }

    // Compute the current URL state
    const currentQueryString = JSON.stringify(router.query);

    // Skip if the query hasn't changed
    if (currentQueryString === lastQueryString.current) return;

    // Update URL with current state

    const newQuery: DashQueryParams = {
      dashboard_tab: page,
      office: office ?? null,
      selected_section: selectedSectionId ?? null,
      selected_page: selectedPageId ?? null
    }

    // Only update URL if query actually changed
    const newQueryString = JSON.stringify(newQuery);
    if (newQueryString !== currentQueryString) {
      lastQueryString.current = newQueryString;
      const newQueryParams = new URLSearchParams();
      Object.entries(newQuery).forEach(([key, value]) => {
        if (value) {
          newQueryParams.set(key, value);
        }
      });

      // use window.history.replaceState to avoid rerender trigger
      // router.push({ query: newQuery }, undefined, { shallow: true });
      window.history.replaceState(null, '', `?${newQueryParams.toString()}`);
    }
  }, [router.isReady, page, office, selectedSectionId, selectedPageId]);

  // Load state from URL on initial load - only run when router is ready
  // and only for Site Info tab
  useEffect(() => {
    if (!router.isReady) return;

    // Don't run if we're already updating from URL (prevent loops)
    if (isUpdatingFromUrl.current) return;

    try {
      isUpdatingFromUrl.current = true;

      const {
        dashboard_tab,
        selected_section,
        selected_page,
        office: officeParam,
      } = router.query as DashQueryParams;

      // Set dashboard tab/page from URL
      if (dashboard_tab && typeof dashboard_tab === 'string') {
        const validTab = dashboard_tab as PageOptions;
        if (
          validTab === 'Attendance' ||
          validTab === 'Visitors' ||
          validTab === 'News' ||
          validTab === 'SiteInfo'
        ) {
          setPage(validTab);
        }
      }

      if(officeParam && typeof officeParam === 'string' && office !== officeParam) {
        setOffice(officeParam);
        saveUserSettings.mutate({
          type: UserPropertyType.LastOfficesFilter,
          value: JSON.stringify([officeParam])
        });
        setUserContext({
          type: 'updateCurrentOffice',
          payload: officeParam
        });

      }

      if (
        selected_section &&
        typeof selected_section === 'string' &&
        selectedSectionId !== selected_section
      )
        setSelectedSectionId(selected_section);
      if (selected_page && typeof selected_page === 'string' && selectedPageId !== selected_page)
        setSelectedPageId(selected_page);

      if ((selected_section || selected_page) && page !== 'SiteInfo') setPage('SiteInfo');
    } finally {
      // Always reset the flag when done
      isUpdatingFromUrl.current = false;
    }
  }, [router.isReady, router.query]);

  const filters: DashboardFilters = {
    weekDate,
    week,
    teams,
    office,
    users,
    currentPage,
    selectedPageId,
    selectedSectionId,
    dateToSortBy,
    dateSortOrder
  };

  const setSelectedPageIdWrapper = (pageId: string | undefined) => {
    setSelectedPageId(pageId);
  };

  const setCurrentPageWrapper = (page: string | undefined) => {
    if (page !== undefined) {
      setCurrentPage(page);
    }
  };

  const setSelectedSectionIdWrapper = (
    sectionId: string | null | undefined
  ) => {
    setSelectedSectionId(sectionId);
  };

  const setPageWrapper = (newPage: PageOptions | null) => {
    if (newPage !== null) {
      setPage(newPage);
    }
  };

  const contextValue = {
    page,
    setPage: setPageWrapper,
    selectedEvent,
    setSelectedEvent: (event: any) => {
      setSelectedEvent(event);
      agendaDisclosure.close();
    },
    filters,
    setWeekDate,
    setTeams: (teams: string[]) => {
      saveUserSettings.mutate({
        type: UserPropertyType.LastTeamsFilter,
        value: JSON.stringify(teams)
      });
      setTeams(teams);
    },
    setOffice: (office: string | null) => {
      saveUserSettings.mutate({
        type: UserPropertyType.LastOfficesFilter,
        value: JSON.stringify([office])
      });
      setOffice(office);
    },
    setUsers: (users: string[]) => {
      saveUserSettings.mutate({
        type: UserPropertyType.LastUsersFilter,
        value: JSON.stringify(users)
      });
      setUsers(users);
    },
    setCurrentPage: setCurrentPageWrapper,
    setSelectedPageId: setSelectedPageIdWrapper,
    setSelectedSectionId: setSelectedSectionIdWrapper,
    agendaDisclosure,
    sortByDate,
    toggleSortOrder
  };

  return (
    <DashboardFiltersContext.Provider value={contextValue}>
      {children}
    </DashboardFiltersContext.Provider>
  );
};

export const useDashboardFilters = (): DashboardFiltersContextProps => {
  const context = useContext(DashboardFiltersContext);
  if (!context) {
    // keep this fallback for design switching
    // design switch is going to be removed
    return {
      selectedEvent: null,
      page: 'Attendance',
      setPage: () => {},
      filters: dashboardStateFallbackValues,
      setSelectedEvent: () => {},
      setWeekDate: () => {},
      setTeams: () => {},
      setOffice: () => {},
      setUsers: () => {},
      setCurrentPage: () => {},
      setSelectedPageId: () => {},
      setSelectedSectionId: () => {},
      sortByDate: () => {},
      toggleSortOrder: () => {}
    };
    // after design switch removal, uncomment this lines
    // throw new Error(
    //   'useDashboardFilters must be used within a DashboardStateProvider'
    // );
  }
  return context;
};
